// === /admin — 트래픽 대시보드 ==========================================
// GA4 데이터를 adminGaReport 프록시(functions/ga.js)로 받아 그린다.
// 이 페이지의 이메일 가드는 코스메틱 — 진짜 벽은 서버의 ADMIN_EMAILS.
// 차트는 의존성 없이 인라인 SVG (drape Admin.jsx 방식).
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { auth } from '../firebase.js';
import { SUPPORTED_LANGS } from '../hooks/useLocale.jsx';
import { AnnouncementAdmin } from './AdminAnnouncement.jsx';

const ADMIN_EMAILS = ['uihyunkei@gmail.com'];
const FN_URL = 'https://us-central1-voda-7647c.cloudfunctions.net/adminGaReport';
const PS_URL = 'https://us-central1-voda-7647c.cloudfunctions.net/adminProductStats';

// 이미지 원가 추정 — 성공 생성 1건 = 이미지 1장 기준. 모델 전환 이력을 날짜로
// 반영한다 (실청구는 GCP 콘솔 결제 리포트가 정답, 이건 곡선 확인용 추정치).
const IMAGE_PRICE_HISTORY = [
  { from: '0000-00-00', perImage: 0.134 }, // gemini-3-pro-image
  { from: '2026-09-15', perImage: 0.067 }, // gemini-3.1-flash-image 전환
];
function imagePriceOn(day) {
  let p = IMAGE_PRICE_HISTORY[0].perImage;
  for (const h of IMAGE_PRICE_HISTORY) if (day >= h.from) p = h.perImage;
  return p;
}

// 수익화 분기점 — 목표값의 근거는 MONETIZATION.md. 여기 숫자를 바꾸면 문서도.
// ①② 는 비율, ③ 만 절대값이다 (2026-09-30 개정). 절대 인원 기준은 유입이
// 정체되면 수요가 있어도 영영 못 넘는다 — 광고만 진짜 규모 게임이라 예외.
// 표본(생성 사용자 수)이 작으면 비율이 우연일 수 있어 도달로 치지 않는다.
const MILESTONES = [
  {
    key: 'packs',
    label: '① 크레딧 팩 출시',
    target: 25,
    read: (ps) => {
      const cur = ps?.consumption?.limitRate7;
      const prev = ps?.consumption?.limitRatePrev7;
      if (!cur) return null;
      return {
        value: cur.pct,
        text: `${cur.pct}% (${cur.hit}/${cur.gen}명)`,
        detail: prev ? `직전 7일 ${prev.pct}% (${prev.hit}/${prev.gen}명)` : null,
        // 2주 연속 유지해야 도달 — 한 주 튀는 건 무시
        hit: cur.gen >= 50 && cur.pct >= 25 && !!prev && prev.gen >= 50 && prev.pct >= 25,
      };
    },
    desc: '최근 7일 생성한 사용자 중 하루라도 무료 한도를 채운 비율 (어제까지). 25% 이상이 2주 연속이면 소모성 IAP·Stripe 팩을 연다. 표본 50명 미만은 불인정. GA credit_exhausted 도 함께 볼 것.',
  },
  {
    key: 'subs',
    label: '② 구독 부활',
    target: 25,
    read: (ps) => {
      const g = ps?.generation;
      if (!g || g.repeatPct === undefined) return null;
      return {
        value: g.repeatPct,
        text: `${g.repeatPct}% (${g.repeatUsers}/${g.generatingUsers}명)`,
        detail: `습관 사용자(8일+) ${g.habitualUsers}명 / 10명`,
        hit: g.generatingUsers >= 100 && g.repeatPct >= 25 && g.habitualUsers >= 10,
      };
    },
    desc: '최근 28일 생성한 사용자 중 서로 다른 2일 이상 생성한 비율. 25% 이상이고 습관 사용자(8일+)가 10명 이상일 때 Pro 를 다시 연다. 구독은 리텐션을 만드는 게 아니라 있는 리텐션에 값을 매기는 장치다.',
  },
  {
    key: 'ads',
    label: '③ 보상형 광고',
    target: 1000,
    read: (ps, avgDau) => ({
      value: avgDau,
      text: `${avgDau.toLocaleString()} DAU`,
      detail: null,
      hit: avgDau >= 1000,
    }),
    desc: '최근 7일 평균 DAU. 1,000을 넘고 팩 전환이 저조한 소진자가 수백 명일 때만 — 그 전엔 광고 안 붙인다.',
  },
];

const PRESETS = [
  { label: '7일', days: 7 },
  { label: '14일', days: 14 },
  { label: '28일', days: 28 },
  { label: '90일', days: 90 },
];

// GA 는 property 타임존(Asia/Seoul 아님 주의 — Firebase 기본은 US) 기준이지만
// 프리셋 용도로는 로컬 날짜면 충분하다.
function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function delta(cur, prev) {
  if (!prev) return null;
  return Math.round(((cur - prev) / prev) * 100);
}

function fmtSec(s) {
  if (!s) return '0분';
  const m = Math.round(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}시간 ${m % 60}분` : `${m}분`;
}

function StatCard({ label, value, prev }) {
  const d = delta(value, prev);
  return (
    <div className="adm-card">
      <div className="adm-card-label">{label}</div>
      <div className="adm-card-value">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      {d !== null && (
        <div className={`adm-card-delta ${d >= 0 ? 'up' : 'down'}`}>
          {d >= 0 ? '+' : ''}{d}% <span>이전 기간 대비</span>
        </div>
      )}
    </div>
  );
}

// 플랫폼별 스택 바 차트. 일별 활성 사용자.
const PLATFORMS = [
  { key: 'web', label: '웹', color: '#121212' },
  { key: 'iOS', label: 'iOS', color: '#8A7A6A' },
  { key: 'Android', label: 'Android', color: '#C6BBAE' },
];

function DailyChart({ daily }) {
  const W = 720; const H = 200; const PAD = 24;
  const max = Math.max(1, ...daily.map((d) => d.web + d.iOS + d.Android));
  const bw = (W - PAD * 2) / Math.max(1, daily.length);
  return (
    <div className="adm-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H + 24}`} className="adm-chart" role="img" preserveAspectRatio="xMidYMid meet">
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={PAD} x2={W - PAD} y1={H - H * f + 8} y2={H - H * f + 8} className="adm-grid" />
            <text x={PAD - 4} y={H - H * f + 12} className="adm-axis" textAnchor="end">{Math.round(max * f)}</text>
          </g>
        ))}
        {daily.map((d, i) => {
          let y = H + 8;
          const x = PAD + i * bw + bw * 0.15;
          return (
            <g key={d.day}>
              {PLATFORMS.map((p) => {
                const h = (d[p.key] / max) * H;
                y -= h;
                return h > 0 ? <rect key={p.key} x={x} y={y} width={bw * 0.7} height={h} fill={p.color} rx={1} /> : null;
              })}
              {(daily.length <= 31 || i % 7 === 0) && (i % Math.ceil(daily.length / 14) === 0) && (
                <text x={x + bw * 0.35} y={H + 22} className="adm-axis" textAnchor="middle">{d.day.slice(5)}</text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="adm-legend">
        {PLATFORMS.map((p) => (
          <span key={p.key}><i style={{ background: p.color }} />{p.label}</span>
        ))}
      </div>
    </div>
  );
}

function Milestones({ ps, daily }) {
  // ③ 은 GA 일별에서, ①② 는 Firestore 집계에서.
  const last7 = daily.slice(-7);
  const avgDau = last7.length ? Math.round(last7.reduce((s, d) => s + d.users, 0) / last7.length) : 0;
  return (
    <section className="adm-panel">
      <h2>수익화 분기점 <span className="adm-sub">목표 근거는 MONETIZATION.md</span></h2>
      <div className="adm-milestones">
        {MILESTONES.map((m) => {
          const r = m.read(ps, avgDau);
          const pct = r ? Math.min(100, Math.round((r.value / m.target) * 100)) : 0;
          const unit = m.key === 'ads' ? ' DAU' : '%';
          return (
            <div key={m.key} className="adm-milestone">
              <div className="adm-milestone-head">
                <strong>{m.label}{r?.hit && ' — 도달'}</strong>
                <span className={r?.hit ? 'hit' : ''}>
                  {r ? r.text : '—'} / {m.target.toLocaleString()}{unit}
                </span>
              </div>
              <div className="adm-milestone-bar"><i style={{ width: `${pct}%` }} className={r?.hit ? 'hit' : ''} /></div>
              {r?.detail && <p className="adm-milestone-detail">{r.detail}</p>}
              <p>{m.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function KV({ rows }) {
  return (
    <dl className="adm-kv">
      {rows.map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd>{typeof v === 'number' ? v.toLocaleString() : v}</dd></div>
      ))}
    </dl>
  );
}

// max 를 주면 그 줄까지만 보이고 나머지는 펼치기로 — 국가·도시처럼 긴 표용.
function Table({ title, sub, cols, rows, max }) {
  const [open, setOpen] = useState(false);
  const shown = max && !open ? rows.slice(0, max) : rows;
  return (
    <section className="adm-panel">
      <h2>{title}{sub && <span className="adm-sub">{sub}</span>}</h2>
      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i}>{r.map((v, j) => <td key={j}>{typeof v === 'number' ? v.toLocaleString() : v}</td>)}</tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cols.length} className="adm-empty">데이터 없음</td></tr>}
          </tbody>
        </table>
      </div>
      {max && rows.length > max && (
        <button type="button" className="adm-more" onClick={() => setOpen((o) => !o)}>
          {open ? '접기' : `전체 ${rows.length}개 보기`}
        </button>
      )}
    </section>
  );
}

const pct = (n, d) => (d ? `${((n / d) * 100).toFixed(1)}%` : '—');
const rate = (x) => (x == null ? '—' : `${(x * 100).toFixed(1)}%`);

// 비율 카드 — 이전 기간과 %p 차이. lowerIsBetter 면 내려간 게 초록.
function RateCard({ label, value, prev, note, lowerIsBetter }) {
  const d = value != null && prev != null ? Math.round((value - prev) * 1000) / 10 : null;
  const good = d !== null && (lowerIsBetter ? d <= 0 : d >= 0);
  return (
    <div className="adm-card">
      <div className="adm-card-label">{label}</div>
      <div className="adm-card-value">{rate(value)}</div>
      {d !== null && (
        <div className={`adm-card-delta ${good ? 'up' : 'down'}`}>
          {d >= 0 ? '+' : ''}{d}%p <span>이전 기간 대비</span>
        </div>
      )}
      {note && <div className="adm-card-note">{note}</div>}
    </div>
  );
}

const OS_LABEL = { ios: 'iPhone · iPad', android: 'Android', desktop: '데스크톱', other: '기타' };

function LandingFunnel({ lp, days }) {
  const q = lp.quality;
  const pq = lp.prevQuality;
  const desktop = lp.byOs.find((o) => o.os === 'desktop');
  const ctrNote = lp.ctrFrom > lp.trackedFrom ? null : `${lp.ctrFrom.slice(5)} 기록 시작 이후 ${lp.visitors}명 기준`;
  return (
    <>
      <section className="adm-panel">
        <h2>랜딩 페이지 <span className="adm-sub">archelier.co/landing · 이 페이지로 세션을 시작한 방문자</span></h2>
        <div className="adm-cards adm-cards-tight">
          <StatCard label={`랜딩 방문자 (${days}일)`} value={q?.users ?? 0} prev={pq?.users} />
          <RateCard label="이탈률 (GA)" value={q?.bounce} prev={pq?.bounce} lowerIsBetter />
          <StatCard label="평균 세션 시간" value={fmtSec(q?.avgSec)} prev={null} />
          <RateCard label="스토어 배지 클릭률" value={lp.visitors ? lp.badgeUsers / lp.visitors : null} note={`${lp.badgeUsers}명 / ${lp.visitors}명${ctrNote ? ` · ${ctrNote}` : ''}`} />
          <RateCard label="웹에서 계속 (데스크톱)" value={desktop?.visitors ? desktop.webUsers / desktop.visitors : null} note={`${lp.webUsers}명 / 데스크톱 ${desktop?.visitors ?? 0}명`} />
          <RateCard label="아무것도 안 누르고 떠남" value={lp.visitors ? 1 - lp.anyUsers / lp.visitors : null} note="배지·웹 둘 다 안 누른 비율" lowerIsBetter />
        </div>
        <p className="adm-note">
          GA 이탈률은 "10초 미만 + 1페이지 + 전환 이벤트 없음" 세션이다. 스토어 배지는 외부로 나가는 링크라
          눌러도 10초 안에 떠나면 이탈로 잡힌다. 실제로 놓친 방문자는 <b>아무것도 안 누르고 떠남</b> 쪽이 더 정확하다.
          배지는 store 파라미터가 GA 맞춤 측정기준으로 등록돼 있지 않아 기기 OS 로 나눴다.
        </p>
      </section>

      <div className="adm-grid2">
        <Table
          title="기기별 랜딩 전환"
          sub={`${lp.ctrFrom} 이후`}
          cols={['기기', '방문자', '이탈률', '배지 클릭', '클릭률', '웹 시작']}
          rows={lp.byOs.map((o) => [OS_LABEL[o.os], o.visitors, rate(o.bounce), o.badgeUsers, pct(o.badgeUsers, o.visitors), o.webUsers])}
        />
        <Table
          title="국가별 랜딩 전환"
          sub={`${lp.ctrFrom} 이후 · 방문자 상위`}
          cols={['국가', '방문자', '이탈률', '배지', '웹']}
          rows={lp.byCountry.map((c) => [c.country, c.visitors, rate(c.bounce), c.badgeUsers, c.webUsers])}
          max={10}
        />
      </div>

      <Table
        title="일별 랜딩"
        sub={`배지·웹 클릭은 ${lp.trackedFrom} 부터 기록`}
        cols={['날짜', '방문자', '이탈률', '배지 클릭', '클릭률', '웹 시작']}
        rows={[...lp.daily].reverse().map((d) => [
          d.day, d.visitors, rate(d.bounce),
          d.day < lp.trackedFrom ? '—' : d.badgeUsers,
          d.day < lp.trackedFrom ? '—' : pct(d.badgeUsers, d.visitors),
          d.day < lp.trackedFrom ? '—' : d.webUsers,
        ])}
        max={14}
      />
    </>
  );
}

// GA 의 language 는 기기·브라우저 언어(영어 이름)다. 앱이 그 언어를 지원하는지
// 로케일 코드에서 이름을 뽑아 대조한다 — 로케일을 추가하면 저절로 따라온다.
const SUPPORTED_LANG_NAMES = (() => {
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'language' });
    return new Set(SUPPORTED_LANGS.map((c) => dn.of(c)));
  } catch {
    return new Set();
  }
})();

const NVR_LABEL = { new: '신규', returning: '재방문' };

function Audience({ a, days }) {
  const total = a.continents.reduce((s, c) => s + c.users, 0);
  return (
    <>
      <h2 className="adm-section-title">사용자 분석 <span className="adm-sub">최근 {days}일 · 웹 + iOS + Android</span></h2>
      <div className="adm-grid3">
        <section className="adm-panel">
          <h2>대륙</h2>
          <KV rows={a.continents.map((c) => [c.continent === '(not set)' ? '(알 수 없음)' : c.continent, `${c.users.toLocaleString()} · ${pct(c.users, total)}`])} />
        </section>
        <section className="adm-panel">
          <h2>기기</h2>
          <KV rows={a.devices.slice(0, 7).map((d) => [`${d.category} · ${d.os}`, d.users])} />
        </section>
        <section className="adm-panel">
          <h2>신규 vs 재방문</h2>
          <KV rows={a.newVsReturning.filter((n) => NVR_LABEL[n.kind]).map((n) => [NVR_LABEL[n.kind], `${n.users.toLocaleString()}명 · 세션 ${n.sessions.toLocaleString()}`])} />
        </section>
      </div>

      <Table
        title="국가"
        sub="생성 전환 = 디자인을 1번 이상 생성한 사용자 / 사용자"
        cols={['국가', '사용자', '신규', '참여율', '생성자', '생성 전환', '웹', 'iOS', 'Android']}
        rows={a.countries.map((c) => [
          c.country, c.users, c.newUsers, rate(c.engagement), c.generators, pct(c.generators, c.users), c.web, c.iOS, c.Android,
        ])}
        max={15}
      />
      <div className="adm-grid2">
        <Table
          title="언어"
          sub="기기·브라우저 언어 · ✓ = 앱 번역 있음"
          cols={['언어', '지원', '사용자', '신규', '참여율', '생성 전환']}
          rows={a.languages.map((l) => [
            l.language, SUPPORTED_LANG_NAMES.has(l.language) ? '✓' : '', l.users, l.newUsers, rate(l.engagement), pct(l.generators, l.users),
          ])}
          max={15}
        />
        <Table
          title="도시"
          cols={['도시', '사용자', '신규']}
          rows={a.cities.map((c) => [
            c.country === '(not set)' ? '(알 수 없음)' : c.city === '(not set)' ? `${c.country} (도시 불명)` : `${c.city}, ${c.country}`,
            c.users, c.newUsers,
          ])}
          max={15}
        />
      </div>
    </>
  );
}

// 화면 이름 — ScreenTracker(App.jsx) 가 보내는 경로 → 사람이 읽는 이름.
const SCREEN_LABELS = {
  '/create': '만들기',
  '/gallery': '갤러리',
  '/designs': '내 페이지',
  '/gio': '디자이너 (Gio)',
  '/account': '설정',
  '/designs/:id': '디자인 상세',
  '/invite': '초대',
  '/landing': '랜딩 (웹)',
  '/s/:id': '공유 링크',
  '/u/:handle': '프로필',
  '/account/blocked': '차단 목록',
  '/bookmarks': '컬렉션 (옛)',
  '/c/:id': '컬렉션 상세 (옛)',
  '/delete-account': '계정 삭제',
  '/privacy': '개인정보처리방침',
  '/terms': '이용약관',
  '/support': '지원',
};
const TAB_KEYS = [['/create', '만들기', '#121212'], ['/gallery', '갤러리', '#C46A4A'], ['/designs', '내 페이지', '#5B7FA6'], ['/gio', '디자이너', '#6E9A62'], ['/account', '설정', '#A3A09A']];

// 탭별 일별 사용자 — 탭 다섯 칸을 선으로.
function TabDailyChart({ daily }) {
  if (!daily?.length) return <p className="adm-note">아직 데이터가 없습니다 (화면 기록은 2.3.0 부터 정확해진다).</p>;
  const W = 720; const H = 180; const P = 24;
  const max = Math.max(1, ...daily.flatMap((d) => TAB_KEYS.map(([k]) => d[k] || 0)));
  const x = (i) => P + (i * (W - P * 2)) / Math.max(1, daily.length - 1);
  const y = (v) => H - P - (v / max) * (H - P * 2);
  return (
    <div className="adm-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="adm-chart" role="img" aria-label="탭별 일별 사용자">
        <line x1={P} x2={W - P} y1={H - P} y2={H - P} className="adm-grid" />
        <text x={P} y={P - 8} className="adm-axis">{max}</text>
        {TAB_KEYS.map(([k, , color]) => (
          <polyline key={k} fill="none" stroke={color} strokeWidth="1.75"
            points={daily.map((d, i) => `${x(i)},${y(d[k] || 0)}`).join(' ')} />
        ))}
        <text x={P} y={H - 6} className="adm-axis">{daily[0].day.slice(5)}</text>
        <text x={W - P} y={H - 6} className="adm-axis" textAnchor="end">{daily[daily.length - 1].day.slice(5)}</text>
      </svg>
      <div className="adm-legend">{TAB_KEYS.map(([k, l, c]) => <span key={k}><i style={{ background: c }} />{l}</span>)}</div>
    </div>
  );
}

function ScreenUsage({ data, days }) {
  if (!data.screens) return null;
  const fmtDelta = (cur, prev) => {
    if (!prev) return '—';
    const d = Math.round(((cur - prev) / prev) * 100);
    return `${d > 0 ? '+' : ''}${d}%`;
  };
  return (
    <>
      <h2 className="adm-section-title">화면별 사용량 <span className="adm-sub">최근 {days}일 · 우리 screen_view 기준 (리다이렉트·자동 추적 제외)</span></h2>
      <section className="adm-panel">
        <h2>탭별 일별 사용자</h2>
        <TabDailyChart daily={data.screenDaily} />
      </section>
      <Table
        title="화면"
        sub="사용자는 플랫폼별 합 · 체류는 그 화면에 머문 시간의 인당 평균"
        cols={['화면', '사용자', '이전 대비', '조회', '인당 조회', '인당 체류', 'iOS', 'Android', '웹']}
        rows={data.screens.map((s) => [
          SCREEN_LABELS[s.screen] ? `${SCREEN_LABELS[s.screen]}  ${s.screen}` : s.screen,
          s.users,
          fmtDelta(s.users, s.prevUsers),
          s.views,
          (s.views / Math.max(1, s.users)).toFixed(1),
          fmtSec(s.engagementSec / Math.max(1, s.users)),
          s.iOS, s.Android, s.web,
        ])}
        max={15}
      />
    </>
  );
}

// === 흐름 탭 ==============================================================
// GA 이벤트로 본 사용자 흐름. 단계마다 그 이벤트를 한 번이라도 보낸 고유 사용자 수라
// 순서를 보장하지는 않는다 (가입이 디자인보다 먼저일 수도). 플랫폼·앱 버전으로 나눠
// 보는 게 핵심 — 버그가 특정 버전에서만 나는지(2.3.0 결제처럼) 바로 보인다.
// 단계에 이벤트가 여럿이면 합친다 (앱 first_open + 웹 first_visit — 겹치지 않는다).
const FLOWS = [
  { key: 'first', title: '처음 → 첫 디자인 → 가입', steps: [
    [['first_open', 'first_visit'], '처음 열기'],
    [['photo_uploaded', 'example_photo_selected'], '사진 올림'],
    [['style_selected'], '스타일 고름'],
    [['design_generated'], '디자인 완성'],
    [['sign_in_completed'], '로그인·가입'],
  ] },
  { key: 'pay', title: '결제', steps: [
    [['sign_in_completed'], '로그인'],
    [['pack_sheet_shown'], '팩 목록'],
    [['pack_purchase_started'], '구매 버튼'],
    [['pack_purchased'], '결제 완료'],
  ], extra: [['credit_exhausted', '크레딧 벽'], ['last_free_hint_shown', '마지막 장 안내'], ['last_free_hint_tap', '안내 → 충전'], ['pack_purchase_cancelled', '구매 취소'], ['pack_purchase_failed', '구매 실패']] },
  { key: 'gio', title: 'Gio', steps: [
    [['nav_tab_gio'], 'Gio 탭'],
    [['gio_chat'], '메시지 보냄'],
    [['gio_action_run'], '만들기·수정 실행'],
  ] },
  { key: 'tour', title: '둘러보기', steps: [
    [['tour_start'], '시작'],
    [['tour_complete'], '끝까지'],
  ], extra: [['tour_skip', '건너뜀']] },
];
const PLATFORM_LABEL = { Android: 'Android', iOS: 'iOS', web: '웹' };

function Flows({ data, days }) {
  const [seg, setSeg] = useState('all');
  // 고를 수 있는 구간: 전체 · 플랫폼 · 앱 플랫폼×버전 (사용자 많은 순)
  const segs = useMemo(() => {
    if (!data) return [];
    const size = (list) => list.filter((r) => r.event === 'session_start').reduce((a, r) => a + r.users, 0);
    const plats = ['Android', 'iOS', 'web']
      .map((p) => ({ id: p, label: PLATFORM_LABEL[p], rows: data.eventsByPlatform.filter((r) => r.platform === p) }))
      .filter((x) => x.rows.length);
    const verKeys = [...new Set(data.eventsByVersion.map((r) => `${r.platform}|${r.version}`))];
    const vers = verKeys.map((k) => {
      const [p, v] = k.split('|');
      return { id: k, label: `${PLATFORM_LABEL[p] || p} ${v || '?'}`, rows: data.eventsByVersion.filter((r) => r.platform === p && r.version === v) };
    }).filter((x) => size(x.rows) > 0).sort((a, b) => size(b.rows) - size(a.rows));
    return [{ id: 'all', label: '전체', rows: data.events }, ...plats, ...vers].map((x) => ({ ...x, size: size(x.rows) }));
  }, [data]);
  if (!data) return <div className="adm-loading">불러오는 중…</div>;
  const cur = segs.find((x) => x.id === seg) || segs[0];
  const users = (names) => names.reduce((a, n) => a + (cur.rows.find((r) => r.event === n)?.users || 0), 0);
  const count = (n) => cur.rows.find((r) => r.event === n)?.count || 0;
  return (
    <>
      <div className="adm-presets adm-seg">
        {segs.map((x) => (
          <button key={x.id} className={cur.id === x.id ? 'active' : ''} onClick={() => setSeg(x.id)}>
            {x.label} <span className="adm-sub">{x.size}</span>
          </button>
        ))}
      </div>
      <div className="adm-grid2">
        {FLOWS.map((f) => {
          const top = users(f.steps[0][0]);
          return (
            <section key={f.key} className="adm-panel">
              <h2>{f.title} <span className="adm-sub">GA · 최근 {days}일 · {cur.label} · 고유 사용자</span></h2>
              <div className="adm-milestones">
                {f.steps.map(([evs, label], i) => {
                  const n = users(evs);
                  const prevN = i ? users(f.steps[i - 1][0]) : null;
                  return (
                    <div key={label} className="adm-milestone">
                      <div className="adm-milestone-head">
                        <strong>{label}</strong>
                        <span>{n.toLocaleString()}명{i > 0 && ` · ${pct(n, top)}`}{prevN !== null && ` (직전의 ${pct(n, prevN)})`}</span>
                      </div>
                      <div className="adm-milestone-bar"><i style={{ width: `${top ? Math.min(100, Math.round((n / top) * 100)) : 0}%` }} /></div>
                    </div>
                  );
                })}
              </div>
              {f.extra && <KV rows={f.extra.map(([e, label]) => [label, `${users([e])}명 · ${count(e)}회`])} />}
            </section>
          );
        })}
      </div>
      <p className="adm-note">
        단계는 그 이벤트를 보낸 고유 사용자 수다 — 순서를 보장하지 않는다. 숫자 옆은 그 구간의 세션 시작 사용자.
        GA 는 하루 정도 늦게 채워진다. 결제 완료는 앱이 스토어 응답을 받은 시점이고 실제 지급은 결제 탭(Firestore)이 정답.
      </p>
    </>
  );
}

// === 결제 탭 ==============================================================
// 실결제는 Firestore creditPurchases (RevenueCat 웹훅이 지급한 기록 — 샌드박스 제외),
// 퍼널은 GA 이벤트 (CreditModal·App.jsx 가 쏜다). GA 는 위 기간 프리셋을 따르고
// Firestore 쪽은 고정 창(7·28일·전체)이다.
const usd = (n) => `$${(n || 0).toFixed(2)}`;
const FUNNEL = [
  ['credit_exhausted', '크레딧 벽 (생성·편집·Gio 막힘)'],
  ['pack_sheet_shown', '팩 목록 노출'],
  ['pack_purchase_started', '구매 버튼 누름'],
  ['pack_purchased', '스토어 결제 완료'],
];

function PayFunnel({ events, days }) {
  const users = (name) => events?.find((e) => e.event === name)?.users || 0;
  const count = (name) => events?.find((e) => e.event === name)?.count || 0;
  const top = users(FUNNEL[0][0]);
  return (
    <section className="adm-panel">
      <h2>크레딧 벽 → 결제 퍼널 <span className="adm-sub">GA · 최근 {days}일 · 고유 사용자 · 앱에서만 판다</span></h2>
      <div className="adm-milestones">
        {FUNNEL.map(([ev, label], i) => {
          const n = users(ev);
          const prevN = i ? users(FUNNEL[i - 1][0]) : null;
          return (
            <div key={ev} className="adm-milestone">
              <div className="adm-milestone-head">
                <strong>{label}</strong>
                <span>{n.toLocaleString()}명 · {pct(n, top)}{prevN !== null && ` (직전 단계의 ${pct(n, prevN)})`}</span>
              </div>
              <div className="adm-milestone-bar"><i style={{ width: `${top ? Math.round((n / top) * 100) : 0}%` }} /></div>
            </div>
          );
        })}
      </div>
      <KV rows={[
        ['구매 취소 / 실패', `${count('pack_purchase_cancelled')}회 / ${count('pack_purchase_failed')}회`],
        ['계정 화면에서 크레딧 열기', `${count('pack_entry')}회 / ${users('pack_entry')}명`],
      ]} />
      <p className="adm-note">
        벽은 웹·게스트도 포함한다 (팩은 로그인한 앱 사용자에게만 뜬다). 그래서 첫 단계 → 노출 비율이 낮게 나온다.
        GA 의 &quot;결제 완료&quot; 는 앱이 스토어 응답을 받은 시점이라, 실제 지급 건수는 아래 Firestore 숫자가 정답이다.
      </p>
    </section>
  );
}

function Payments({ ps, data, days }) {
  const p = ps?.payments;
  if (!p) return <div className="adm-loading">결제 지표 불러오는 중…</div>;
  const w = p.wall;
  const r = p.payRate;
  const open = (p.unclaimed || []).filter((x) => x.status === 'open');
  const handled = (p.unclaimed || []).filter((x) => x.status !== 'open');
  return (
    <>
      {open.length > 0 && (
        <Table
          title={`⚠️ 미지급 결제 ${open.length}건 — 익명 ID 로 결제돼 크레딧이 안 들어갔다`}
          sub="10분마다 자동으로 주인을 찾는다 (사용자가 앱을 다시 켜야 잡힌다). 급하면 uid 를 찾아 수동 지급 후 unclaimedPacks 에 resolvedAt."
          cols={['시각', '상품', '스토어', '거래 ID', '금액', '현지가']}
          rows={open.map((x) => [
            x.at ? x.at.slice(0, 16).replace('T', ' ') : '—', x.productId, x.store, x.transactionId || '—',
            usd(x.usd), x.local != null ? `${x.local} ${x.currency || ''}` : '—',
          ])}
        />
      )}
      <div className="adm-cards">
        <StatCard label="총 매출 (gross)" value={usd(p.live.grossUsd)} prev={null} />
        <StatCard label="순매출 추정 (수수료 후)" value={usd(p.live.netUsd)} prev={null} />
        <StatCard label="최근 28일 매출" value={usd(p.last28.grossUsd)} prev={null} />
        <StatCard label="최근 7일 매출" value={usd(p.last7.grossUsd)} prev={null} />
        <StatCard label="결제 건수 (전체)" value={p.live.count} prev={null} />
        <StatCard label="결제자 (전체)" value={p.live.buyers} prev={null} />
      </div>

      <div className="adm-cards">
        <RateCard label="결제율 (가입자 대비)" value={r.signups ? r.buyers / r.signups : null} note={`결제자 ${r.buyers} / 가입 ${r.signups.toLocaleString()}명 · 전 기간`} />
        <RateCard label="결제율 (28일 생성자 대비)" value={r.generators28 ? r.generators28Buyers / r.generators28 : null} note={`28일 내 산 사람 ${r.generators28Buyers} / 생성자 ${r.generators28}명`} />
        <RateCard label="벽 → 결제 전환" value={w.users ? w.convertedEver / w.users : null} note={`벽 이후 구매 ${w.convertedEver} / 벽 경험 ${w.users}명`} />
        <RateCard label="벽 → 결제 (28일)" value={w.last28Users ? w.converted28 / w.last28Users : null} note={`${w.converted28} / 28일 내 벽 ${w.last28Users}명`} />
        <StatCard label="결제자당 매출 (ARPPU)" value={usd(r.arppu)} prev={null} />
        <StatCard label="재구매자 (2회+)" value={`${p.live.repeatBuyers}명`} prev={null} />
      </div>

      <PayFunnel events={data?.events} days={days} />

      <div className="adm-grid2">
        <section className="adm-panel">
          <h2>크레딧 벽 <span className="adm-sub">서버 기록 · {w.trackedFrom ? `${w.trackedFrom.slice(0, 10)} 부터` : '아직 기록 없음 (functions 배포 후 시작)'}</span></h2>
          <KV rows={[
            ['벽에 부딪힌 사용자 (전체)', w.users],
            ['최근 7일 / 28일', `${w.last7Users} / ${w.last28Users}명`],
            ['벽에 부딪힌 날 합계', `${w.wallDays} user-days`],
            ['인당 벽 일수', w.users ? (w.wallDays / w.users).toFixed(1) : '—'],
            ['벽 → 첫 구매까지 (중앙값)', w.medianHoursToBuy == null ? '—' : `${w.medianHoursToBuy}시간`],
            ['벽 없이 산 사람', `${w.buyersNoWall}명`],
          ]} />
          <p className="adm-note">
            벽 = 남은 크레딧이 생성 1장 값보다 적어진 순간. 사용자당 하루 한 번만 센다 (daily 가 매일 차므로).
            Pro(구독 유지자)는 무제한이라 안 잡힌다.
          </p>
        </section>
        <section className="adm-panel">
          <h2>결제 현황 <span className="adm-sub">Firestore creditPurchases · 샌드박스 제외</span></h2>
          <KV rows={[
            ['지급 크레딧 합계', `${p.live.credits.toLocaleString()} 크레딧`],
            ['환불', `${p.live.refunds}건 · ${usd(p.live.refundedUsd)}`],
            ['최근 28일', `${p.last28.count}건 · ${p.last28.buyers}명 · ${usd(p.last28.grossUsd)}`],
            ['최근 7일', `${p.last7.count}건 · ${p.last7.buyers}명 · ${usd(p.last7.grossUsd)}`],
            ['샌드박스 구매 (테스트)', `${p.sandbox}건`],
            ['Pro 구독 유지 (옛 구독자)', `${p.subs.pro}명${p.subs.trialing ? ` · trial ${p.subs.trialing}` : ''}${p.subs.pastDue ? ` · 결제 문제 ${p.subs.pastDue}` : ''}`],
          ]} />
          <p className="adm-note">금액은 RevenueCat 이 보낸 USD 환산가. 순매출은 RC 수수료 비율, 없으면 15% 로 추정. 실정산은 각 스토어 콘솔이 정답.</p>
        </section>
      </div>

      <div className="adm-grid2">
        <Table
          title="상품별"
          cols={['상품', '건수', '크레딧', '매출']}
          rows={p.byProduct.map((g) => [g.key, g.count, g.credits, usd(g.grossUsd)])}
        />
        <Table
          title="스토어별"
          cols={['스토어', '건수', '매출']}
          rows={p.byStore.map((g) => [g.key, g.count, usd(g.grossUsd)])}
        />
      </div>

      <Table
        title="일별 결제 (최근 28일)"
        cols={['날짜', '건수', '매출']}
        rows={[...p.daily].reverse().map((d) => [d.day, d.count, usd(d.grossUsd)])}
      />
      <Table
        title="최근 결제 20건"
        cols={['시각', '상품', '스토어', '금액', '현지가', '비고']}
        rows={p.recent.map((x) => [
          x.at ? x.at.slice(0, 16).replace('T', ' ') : '—', x.productId, x.store, usd(x.usd),
          x.local != null ? `${x.local} ${x.currency || ''}` : '—',
          [x.sandbox && '샌드박스', x.refunded && '환불', x.recovered && '익명 결제 복구'].filter(Boolean).join(' · ') || '',
        ])}
      />
      {handled.length > 0 && (
        <Table
          title="익명 결제 처리 내역"
          sub="익명 ID 로 들어와 나중에 주인을 찾아 지급한 결제"
          cols={['결제 시각', '상품', '거래 ID', '현지가', '처리', '처리 시각', '보너스', '메모']}
          rows={handled.map((x) => [
            x.at ? x.at.slice(0, 16).replace('T', ' ') : '—', x.productId, x.transactionId || '—',
            x.local != null ? `${x.local} ${x.currency || ''}` : '—',
            { auto: '✓ 자동 지급', manual: '✓ 수동 지급', refunded: '환불됨' }[x.status] + (x.refunded && x.status !== 'refunded' ? ' · 환불 회수' : ''),
            x.resolvedAt ? x.resolvedAt.slice(0, 16).replace('T', ' ') : '—',
            x.bonus ? `+${x.bonus}` : '', x.note || '',
          ])}
        />
      )}
    </>
  );
}

export function Admin({ user }) {
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [ps, setPs] = useState(null); // product stats (크레딧·초대·리텐션, 고정 28일 창)
  const [psError, setPsError] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('traffic');

  const isAdmin = !!user && !user.isAnonymous && ADMIN_EMAILS.includes(user.email || '');

  useEffect(() => {
    if (!isAdmin) return;
    let stale = false;
    (async () => {
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(PS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({}),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || json.error || `HTTP ${res.status}`);
        if (!stale) setPs(json);
      } catch (e) {
        if (!stale) setPsError(String(e.message || e));
      }
    })();
    return () => { stale = true; };
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    let stale = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(FN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ from: isoDaysAgo(days - 1), to: isoDaysAgo(0) }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || json.error || `HTTP ${res.status}`);
        if (!stale) setData(json);
      } catch (e) {
        if (!stale) setError(String(e.message || e));
      } finally {
        if (!stale) setLoading(false);
      }
    })();
    return () => { stale = true; };
  }, [isAdmin, days]);

  // 이벤트에서 요약 카드용 숫자 추출
  const ev = useMemo(() => {
    const pick = (list, name) => list?.find((e) => e.event === name) || { count: 0, users: 0 };
    return {
      installs: pick(data?.events, 'first_open').count,
      prevInstalls: pick(data?.prevEvents, 'first_open').count,
      generated: pick(data?.events, 'design_generated').count,
      prevGenerated: pick(data?.prevEvents, 'design_generated').count,
      signIns: pick(data?.events, 'sign_in_completed').users,
      // 초대 링크 클릭. capturePendingReferral 이 주소창에서 ?ref= 를 지우기
      // 때문에 GA4 의 pageLocation 으로는 안 잡힌다 — 전용 이벤트가 유일한
      // 측정 경로다 (services/referral-service.js).
      refClicks: pick(data?.events, 'referral_link_opened').count,
      refClickUsers: pick(data?.events, 'referral_link_opened').users,
      prevSignIns: pick(data?.prevEvents, 'sign_in_completed').users,
    };
  }, [data]);

  if (user === undefined) return null; // auth 로딩 중
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="adm-page">
      <header className="adm-header">
        <h1>관리자</h1>
        <div className="adm-presets">
          <button className={view === 'traffic' ? 'active' : ''} onClick={() => setView('traffic')}>트래픽</button>
          <button className={view === 'flows' ? 'active' : ''} onClick={() => setView('flows')}>흐름</button>
          <button className={view === 'pay' ? 'active' : ''} onClick={() => setView('pay')}>결제</button>
          <button className={view === 'announce' ? 'active' : ''} onClick={() => setView('announce')}>공지</button>
        </div>
        {view !== 'announce' && <div className="adm-presets">
          {PRESETS.map((p) => (
            <button key={p.days} className={days === p.days ? 'active' : ''} onClick={() => setDays(p.days)}>
              {p.label}
            </button>
          ))}
        </div>}
      </header>

      {view === 'announce' && <AnnouncementAdmin />}
      {view === 'flows' && error && <div className="adm-error">불러오기 실패: {error}</div>}
      {view === 'flows' && <div className={loading ? 'adm-stale' : ''}><Flows data={data} days={days} /></div>}
      {view === 'pay' && psError && <div className="adm-error">결제 지표 불러오기 실패: {psError}</div>}
      {view === 'pay' && <Payments ps={ps} data={data} days={days} />}

      {view === 'traffic' && error && <div className="adm-error">불러오기 실패: {error}</div>}
      {view === 'traffic' && loading && !data && <div className="adm-loading">불러오는 중…</div>}

      {view === 'traffic' && data && (
        <div className={loading ? 'adm-stale' : ''}>
          <div className="adm-cards">
            <StatCard label="활성 사용자" value={data.totals.users} prev={data.prevTotals.users} />
            <StatCard label="신규 사용자" value={data.totals.newUsers} prev={data.prevTotals.newUsers} />
            <StatCard label="세션" value={data.totals.sessions} prev={data.prevTotals.sessions} />
            <StatCard label="앱 설치" value={ev.installs} prev={ev.prevInstalls} />
            <StatCard label="디자인 생성" value={ev.generated} prev={ev.prevGenerated} />
            <StatCard label="로그인 사용자" value={ev.signIns} prev={ev.prevSignIns} />
            <StatCard
              label="인당 체류시간"
              value={fmtSec(data.totals.engagementSec / Math.max(1, data.totals.users))}
              prev={null}
            />
          </div>

          <ScreenUsage data={data} days={days} />

          <Milestones ps={ps} daily={data.daily} />
          {psError && <div className="adm-error">제품 지표 불러오기 실패: {psError}</div>}

          {ps && (
            <div className="adm-grid2">
              <section className="adm-panel">
                <h2>크레딧 (오늘) <span className="adm-sub">Firestore 실시간</span></h2>
                <KV rows={[
                  ['오늘 소비한 사용자', ps.credits.consumersToday],
                  ['오늘 소비 총량', `${ps.credits.usedTodayTotal} 크레딧`],
                  ['인당 소비 (소비자 기준)', ps.credits.consumersToday ? (ps.credits.usedTodayTotal / ps.credits.consumersToday).toFixed(1) : '—'],
                  ['소비 분포 1 / 2 / 3+', `${ps.credits.usedDist[1]} / ${ps.credits.usedDist[2]} / ${ps.credits.usedDist[3]}`],
                  ['무료 한도 도달', ps.credits.hitDailyLimitToday ?? '—'],
                  ['완전 소진 (보너스도 0)', ps.credits.exhaustedToday],
                  ['미소비 보너스 잔액 합', `${ps.credits.bonusOutstanding} 크레딧`],
                ]} />
              </section>
              <section className="adm-panel">
                <h2>초대 코드 <span className="adm-sub">성사는 전 기간 누적 · 클릭은 최근 {days}일</span></h2>
                <KV rows={[
                  /* 클릭은 GA4(기간 한정), 성사는 Firestore(전 기간) — 기간이
                     다르므로 전환율은 어림값이다. 그래도 "아무도 안 눌렀는지"
                     와 "누르고 이탈했는지" 를 구분하는 데는 이게 유일하다. */
                  [`초대 링크 클릭 (최근 ${days}일)`, ev.refClicks ? `${ev.refClicks}회 / ${ev.refClickUsers}명` : '0 — 2026-09-21 부터 집계'],
                  ['초대로 가입한 계정', ps.invites.redeemedTotal],
                  ['클릭 → 성사 전환율', ev.refClicks ? `${(ps.invites.redeemedLast28 / ev.refClicks * 100).toFixed(1)}% (28일 성사 / 기간 클릭, 어림값)` : '—'],
                  ['최근 28일 성사', `${ps.invites.redeemedLast28} (8/25 이후 집계)`],
                  ['초대 성사시킨 계정', ps.invites.inviters],
                  ['초대 성사 합계', ps.invites.invitesSent],
                  ['상한(50회) 도달', ps.invites.cappedInviters],
                ]} />
              </section>
              <section className="adm-panel">
                <h2>생성 리텐션 <span className="adm-sub">최근 {ps.generation.windowDays}일</span></h2>
                <KV rows={[
                  ['생성한 사용자', ps.generation.generatingUsers],
                  ['반복 생성 (2일+)', ps.generation.repeatUsers],
                  ['습관 사용자 (8일+)', ps.generation.habitualUsers],
                  ['성공 생성 수', ps.generation.successCount],
                  ['생성일 하루 인당 크레딧', ps.generation.creditsPerActiveDay],
                ]} />
              </section>
              {ps.consumption && (
                <section className="adm-panel">
                  <h2>크레딧 소진율 <span className="adm-sub">사용자×일 기준, 28일</span></h2>
                  <KV rows={[
                    ['인당 하루 평균 소비', `${ps.consumption.avgPerUserDay} 크레딧`],
                    ['소비 분포 1 / 2 / 3 / 4+', `${ps.consumption.dist[1]} / ${ps.consumption.dist[2]} / ${ps.consumption.dist[3]} / ${ps.consumption.dist.more}`],
                    ['무료 한도 도달율', `${ps.consumption.atLimitPct}%`],
                    ['한도 도달자 7일 평균', `${ps.consumption.limitHittersAvg7 ?? '—'}명/일`],
                    ['한도 넘겨 소비 (보너스)', `${ps.consumption.dist.more} user-days`],
                  ]} />
                </section>
              )}
              <section className="adm-panel">
                <h2>가입 <span className="adm-sub">전체 기간 누적, 익명 제외</span></h2>
                <KV rows={[
                  ['총 가입 계정', ps.signups?.total ?? ps.credits.totalUsers],
                  ['오늘', ps.signups?.today ?? '—'],
                  ['최근 7일', ps.signups?.last7 ?? '—'],
                  ['최근 28일', ps.signups?.last28 ?? '—'],
                  ['익명(게스트) — 전체', ps.signups?.anon?.total ?? ps.signups?.anonSessions ?? '—'],
                  ['익명 — 오늘 / 7일 / 28일', ps.signups?.anon ? `${ps.signups.anon.today} / ${ps.signups.anon.last7} / ${ps.signups.anon.last28}` : '—'],
                ]} />
              </section>
            </div>
          )}

          {ps?.promoCandidates?.length > 0 && (
            <Table
              title="스타일 승격 후보 (2명+ 재사용 커스텀)"
              cols={['프롬프트', '카테고리', '사용자', '주간', '작성자']}
              rows={ps.promoCandidates.map((c) => [
                c.prompt.length > 60 ? `${c.prompt.slice(0, 60)}…` : c.prompt,
                c.spaceCategory, c.users, c.weeklyUsers, c.handle ? `@${c.handle}` : '(게스트/핸들 없음)',
              ])}
            />
          )}

          <section className="adm-panel">
            <h2>일별 활성 사용자 <span className="adm-sub">{data.range.from} ~ {data.range.to}</span></h2>
            <DailyChart daily={data.daily} />
          </section>

          {data.landingFunnel && <LandingFunnel lp={data.landingFunnel} days={days} />}
          {data.audience && <Audience a={data.audience} days={days} />}

          {ps?.consumption && (() => {
            const daily = ps.consumption.daily;
            const cost = (d) => d.gens * imagePriceOn(d.day);
            const last7 = daily.slice(-8, -1); // 오늘(진행 중) 제외 7일
            const prev7 = daily.slice(-15, -8);
            const sum = (arr) => arr.reduce((s, d) => s + cost(d), 0);
            return (
              <>
                <section className="adm-panel">
                  <h2>이미지 원가 (추정) <span className="adm-sub">생성 수 × 모델 단가 · 9/15 flash 전환 반영 · 실청구는 GCP 콘솔</span></h2>
                  <KV rows={[
                    ['최근 7일 (어제까지)', `$${sum(last7).toFixed(2)}`],
                    ['그 전 7일', `$${sum(prev7).toFixed(2)}`],
                    ['월 런레이트 (최근 7일 기준)', `$${(sum(last7) / 7 * 30).toFixed(0)}`],
                    ['현재 장당 단가', `$${imagePriceOn('9999-12-31')}`],
                  ]} />
                </section>
                <Table
                  title="일별 가입 · 크레딧 소비 (최근 28일)"
                  cols={['날짜', '가입', '생성자', '생성 수', '인당', '원가($)']}
                  rows={(() => {
                    const su = new Map((ps.signups?.daily || []).map((d) => [d.day, d.n]));
                    const days = new Map(daily.map((d) => [d.day, d]));
                    const all = [...new Set([...su.keys(), ...days.keys()])].sort().reverse();
                    return all.map((day) => {
                      const c = days.get(day);
                      return [day, su.get(day) || 0, c?.users ?? 0, c?.gens ?? 0, c?.perUser ?? '—',
                        c ? cost(c).toFixed(2) : '—'];
                    });
                  })()}
                />
              </>
            );
          })()}

          <div className="adm-grid2">
            <Table
              title="유입 경로"
              cols={['소스 / 매체', '사용자', '세션']}
              rows={data.sources.map((s) => [`${s.source} / ${s.medium}`, s.users, s.sessions])}
            />
            {!data.audience && (
              <Table
                title="국가"
                cols={['국가', '사용자', '신규']}
                rows={data.countries.map((c) => [c.country, c.users, c.newUsers])}
              />
            )}
            <Table
              title="랜딩 페이지"
              cols={['페이지', '사용자', '세션']}
              rows={data.landing.map((l) => [l.page || '(없음)', l.users, l.sessions])}
            />
            <Table
              title="이벤트"
              cols={['이벤트', '횟수', '사용자']}
              rows={data.events.map((e) => [e.event, e.count, e.users])}
            />
          </div>
        </div>
      )}
    </div>
  );
}
